import { c as _c } from "react/compiler-runtime";
import { useEffect, type RefObject } from "react";
import type { VideoProps } from "@/types";
import { detectPlaybackCapabilities, selectPlayback, type VideoPlayback } from "./selectPlayback";
type StopPlayback = () => void;

/**
 * Attaches the transcoded HLS stream to a video element once it is active.
 * Any failure falls back to the original file, so a broken playlist never
 * leaves an empty box behind.
 */
export function useHlsPlayback(elementRef, video, isActive) {
  const $ = _c(6);
  const {
    hlsSrc,
    src
  } = video;
  let t0;
  let t1;
  if ($[0] !== elementRef || $[1] !== hlsSrc || $[2] !== isActive || $[3] !== src) {
    t0 = () => {
      const element = elementRef.current;
      if (!element || !isActive || !hlsSrc) {
        return;
      }
      const playback = selectPlayback(hlsSrc, detectPlaybackCapabilities(element));
      return startPlayback(playback, element, hlsSrc, src);
    };
    t1 = [elementRef, isActive, hlsSrc, src];
    $[0] = elementRef;
    $[1] = hlsSrc;
    $[2] = isActive;
    $[3] = src;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
}
function startPlayback(playback: VideoPlayback, element: HTMLVideoElement, hlsSrc: string, originalSrc: string): StopPlayback {
  if (playback === "native-hls") {
    return playNatively(element, hlsSrc, originalSrc);
  }
  if (playback === "hls-js") {
    return playWithHlsJs(element, hlsSrc, originalSrc);
  }
  element.src = originalSrc;
  return () => {};
}
function playNatively(element: HTMLVideoElement, hlsSrc: string, originalSrc: string): StopPlayback {
  const fallBackToOriginal = () => {
    element.src = originalSrc;
  };
  element.addEventListener("error", fallBackToOriginal, {
    once: true
  });
  element.src = hlsSrc;
  return () => element.removeEventListener("error", fallBackToOriginal);
}
function playWithHlsJs(element: HTMLVideoElement, hlsSrc: string, originalSrc: string): StopPlayback {
  let isStopped = false;
  let stopHls: StopPlayback = () => {};
  const fallBackToOriginal = () => {
    stopHls();
    element.src = originalSrc;
  };

  // Loaded on demand: only browsers without native HLS ever download hls.js.
  import("hls.js").then(({
    default: Hls
  }) => {
    if (isStopped) return;
    if (!Hls.isSupported()) return fallBackToOriginal();
    const hls = new Hls({
      capLevelToPlayerSize: true
    });
    stopHls = () => hls.destroy();
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal) fallBackToOriginal();
    });
    hls.loadSource(hlsSrc);
    hls.attachMedia(element);
  }).catch(() => {
    if (!isStopped) fallBackToOriginal();
  });
  return () => {
    isStopped = true;
    stopHls();
  };
}